import { ProcessorDefinition } from '@kaoto/camel-catalog/types';
import { isDefined } from '@kaoto/forms';
import { useVisualizationController } from '@patternfly/react-topology';
import { cloneDeep } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';

import { CatalogModalContext } from '../../../../dynamic-catalog/catalog-modal.provider';
import { SourceSchemaType } from '../../../../models/camel/source-schema-type';
import { EntityType } from '../../../../models/entities';
import { AddStepMode, IVisualizationNode } from '../../../../models/visualization/base-visual-entity';
import { ProcessorStepsService } from '../../../../models/visualization/flows/support/processor-steps.service';
import { EntitiesContext } from '../../../../providers/entities.provider';
import { VisibleFlowsContext } from '../../../../providers/visible-flows.provider';
import { updateIds } from '../../../../utils/update-ids';
import {
  IInteractionType,
  IOnCopyAddon,
  IOnDuplicateAddon,
} from '../../../registers/interactions/node-interaction-addon.model';
import { NodeInteractionAddonContext } from '../../../registers/interactions/node-interaction-addon.provider';
import { processOnCopyAddon, processOnDuplicateAddonRecursively } from '../ContextMenu/item-interaction-helper';

export const useDuplicateStep = (vizNode: IVisualizationNode) => {
  const entitiesContext = useContext(EntitiesContext)!;
  const catalogModalContext = useContext(CatalogModalContext);
  const nodeInteractionAddonContext = useContext(NodeInteractionAddonContext);
  const visibleFlowsContext = useContext(VisibleFlowsContext)!;
  const controller = useVisualizationController();
  let vizNodeContent = vizNode.getCopiedContent();

  if (vizNodeContent) {
    vizNodeContent = processOnCopyAddon(
      vizNode,
      vizNodeContent,
      (vn) =>
        nodeInteractionAddonContext.getRegisteredInteractionAddons(IInteractionType.ON_COPY, vn) as IOnCopyAddon[],
    );
  }
  const parentVizNode = vizNode.getParentNode();

  const canDuplicate = useMemo(() => {
    if (!isDefined(vizNodeContent)) return false;

    // check for step array nodes
    if (vizNode.getNodeInteraction().canHaveNextStep) {
      const filter = entitiesContext.camelResource.getCompatibleComponents(
        AddStepMode.AppendStep,
        vizNode.data,
        vizNode.getNodeDefinition(),
      );

      /** Check paste compatibility */
      return catalogModalContext?.checkCompatibility(vizNodeContent.name, filter) ?? false;
    }

    // check for special children nodes in case of Route Entity
    if (parentVizNode?.getNodeInteraction().canHaveSpecialChildren) {
      const filter = entitiesContext.camelResource.getCompatibleComponents(
        AddStepMode.InsertSpecialChildStep,
        parentVizNode.data,
        parentVizNode.getNodeDefinition(),
      );

      /** Check paste compatibility */
      return catalogModalContext?.checkCompatibility(vizNodeContent.name, filter) ?? false;
    }

    // check for root containers only in case of Route Entity
    if (entitiesContext.camelResource.getType() === SourceSchemaType.RouteYaml && !isDefined(parentVizNode)) {
      return true;
    }

    return false;
  }, [catalogModalContext, entitiesContext, parentVizNode, vizNode, vizNodeContent]);

  const onDuplicate = useCallback(async () => {
    if (!vizNode || !vizNodeContent || !entitiesContext) return;

    let updatedVizNodeContent = updateIds(cloneDeep(vizNodeContent));

    updatedVizNodeContent = await processOnDuplicateAddonRecursively(
      vizNode,
      updatedVizNodeContent,
      (vn) =>
        nodeInteractionAddonContext.getRegisteredInteractionAddons(
          IInteractionType.ON_DUPLICATE,
          vn,
        ) as IOnDuplicateAddon[],
    );

    if (!updatedVizNodeContent) return;

    if (entitiesContext.camelResource.getType() === SourceSchemaType.RouteYaml && !isDefined(parentVizNode)) {
      const originalEntityId = vizNode.getId();
      const newId = entitiesContext.camelResource.addNewEntity(
        updatedVizNodeContent.name as EntityType,
        { [updatedVizNodeContent.name]: updatedVizNodeContent.definition },
        originalEntityId,
      );
      visibleFlowsContext.visualFlowsApi.toggleFlowVisible(newId);
      controller.fromModel({
        nodes: [],
        edges: [],
      });
    } else {
      /** Append the content of the current node on the current node */
      vizNode.pasteBaseEntityStep(updatedVizNodeContent, AddStepMode.AppendStep);

      // Set an empty model to clear the graph, Fixes an issue rendering child nodes incorrectly
      if (parentVizNode?.getNodeInteraction().canHaveSpecialChildren) {
        const stepsProperties = ProcessorStepsService.getProcessorStepsProperties(
          parentVizNode.data.primaryNodeId?.name as keyof ProcessorDefinition,
        );
        if (
          stepsProperties.some(
            (property) =>
              property.type === 'array-clause' &&
              property.name === updatedVizNodeContent.name &&
              isDefined(parentVizNode.getChildren()),
          )
        ) {
          controller.fromModel({
            nodes: [],
            edges: [],
          });
        }
      }
    }

    /** Update entity */
    entitiesContext.updateEntitiesFromCamelResource();
  }, [
    controller,
    entitiesContext,
    nodeInteractionAddonContext,
    parentVizNode,
    visibleFlowsContext.visualFlowsApi,
    vizNode,
    vizNodeContent,
  ]);

  const value = useMemo(
    () => ({
      onDuplicate,
      canDuplicate,
    }),
    [canDuplicate, onDuplicate],
  );

  return value;
};
